@if (loadingCards && !cards.length) {
  <div class="text-center py-4">
    <div class="spinner-border spinner-border-sm text-primary" role="status">
      <span class="visually-hidden">{{
        "attendance.opportunityTab.loading" | translate
      }}</span>
    </div>
  </div>
} @else if (!cards.length) {
  <div class="border border-dashed rounded text-center py-3 px-2">
    <i class="ri-filter-3-line font-36 text-muted"></i>
    <p class="text-muted mt-1 mb-2">
      {{ "attendance.panelInfo.noOpportunityYet" | translate }}
    </p>
    <button
      type="button"
      class="btn btn-primary w-100 rounded-pill"
      (click)="convert()"
    >
      <i class="ri-arrow-left-right-line fs-5 me-1"></i>
      <span>{{ "attendance.panelInfo.convertToOpportunity" | translate }}</span>
    </button>
  </div>
} @else {
  @for (card of cards; track card._id) {
    <div class="card mb-2">
      <!-- Header: title + chevron on the first line, "funnel · stage · value" + badges on the second. -->
      <button
        type="button"
        class="btn w-100 text-start px-3 py-2 shadow-none"
        [attr.aria-expanded]="isExpanded(card)"
        (click)="toggle(card)"
      >
        <span class="d-flex align-items-center gap-2">
          <span
            class="flex-grow-1 min-w-0 font-15 fw-bold text-dark text-truncate"
          >
            {{ card.name }}
          </span>
          <i
            class="font-18 text-muted"
            [ngClass]="
              isExpanded(card) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
            "
          ></i>
        </span>
        <span class="d-flex align-items-center gap-2 font-12 text-muted mt-1">
          <span class="flex-grow-1 min-w-0 text-truncate text-dark">
            {{ card.funnel?.name }}
          </span>
          @if (isOfAttendance(card)) {
            <span class="badge bg-soft-primary text-primary flex-shrink-0">
              {{ "attendance.opportunityTab.thisAttendance" | translate }}
            </span>
          }
          @if (isClosed(card)) {
            <span
              class="badge flex-shrink-0"
              [ngClass]="
                card.won
                  ? 'bg-soft-success text-success'
                  : 'bg-soft-danger text-danger'
              "
            >
              {{
                (card.won
                  ? "kanban.stageColumn.won"
                  : "kanban.stageColumn.lost"
                ) | translate
              }}
            </span>
          }
        </span>
      </button>

      @if (isExpanded(card)) {
        <div class="card-body border-top pt-3">
          @if (isClosed(card)) {
            <div
              class="alert py-2 mb-3"
              [ngClass]="card.won ? 'alert-success' : 'alert-danger'"
              role="alert"
            >
              <i
                class="me-1"
                [ngClass]="
                  card.won ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'
                "
              ></i>
              {{
                (card.won
                  ? "attendance.opportunityTab.wonAt"
                  : "attendance.opportunityTab.lostAt"
                ) | translate: { date: (closedAt(card) | date: "d MMM") }
              }}
            </div>
          }

          <div class="d-flex align-items-center gap-2 mb-2">
            <span class="font-17 fw-bold text-dark">{{
              card.budget ?? 0 | currency: "BRL" : "symbol-narrow" : "1.2-2"
            }}</span>
            <span class="badge bg-soft-primary text-primary">{{
              card.stage?.name
            }}</span>
            <span class="flex-grow-1"></span>
            <img
              class="avatar-xs rounded-circle"
              [src]="getUserImage(card.user ?? null)"
              (error)="onImageError($event)"
              [alt]="card.user?.name ?? ''"
            />
            <span class="font-13 fw-bold text-dark text-truncate">
              {{
                card.user?.name ?? ("kanban.cardItem.noAssignee" | translate)
              }}
            </span>
          </div>

          @if (!isClosed(card) && stagesOf(card).length) {
            <div class="d-flex gap-1 mb-1">
              @for (stage of stagesOf(card); track stage._id; let i = $index) {
                <div class="progress progress-sm flex-grow-1">
                  @if (i <= stageIndex(card)) {
                    <div class="progress-bar w-100"></div>
                  }
                </div>
              }
            </div>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="font-12 fw-bold text-dark">
                {{
                  "attendance.opportunityTab.stagePosition"
                    | translate
                      : {
                          stage: $safeNavigationMigration(card.stage?.name),
                          position: stageIndex(card) + 1,
                          total: stagesOf(card).length,
                        }
                }}
              </span>
              @if (card.daysInStage) {
                <span class="font-11 text-muted">
                  {{
                    "kanban.radar.daysInStage"
                      | translate: { days: card.daysInStage }
                  }}
                </span>
              }
            </div>
            @if (nextStage(card); as next) {
              <button
                type="button"
                class="btn btn-primary w-100 rounded"
                [disabled]="busy"
                (click)="advance(card)"
              >
                {{
                  "attendance.opportunityTab.advanceTo"
                    | translate: { stage: next.name }
                }}
                <i class="ri-arrow-right-line ms-1"></i>
              </button>
            }
            <div class="d-flex gap-2 mt-2">
              @if (wonStageOf(card)) {
                <button
                  type="button"
                  class="btn btn-outline-success flex-grow-1"
                  [disabled]="busy"
                  (click)="markWon(card)"
                >
                  <i class="ri-check-line me-1"></i
                  >{{ "kanban.stageColumn.won" | translate }}
                </button>
              }
              @if (lostStageOf(card)) {
                <button
                  type="button"
                  class="btn btn-outline-danger flex-grow-1"
                  [disabled]="busy"
                  (click)="markLost(card)"
                >
                  <i class="ri-close-line me-1"></i
                  >{{ "kanban.stageColumn.lost" | translate }}
                </button>
              }
            </div>
          }

          <hr class="my-3" />
          <h6 class="text-uppercase text-muted font-11 fw-bold mb-2">
            {{ "attendance.opportunityTab.signals" | translate }}
          </h6>
          <app-card-radar
            [card]="card"
            [panel]="true"
            (scheduleNextAction)="openNextAction(card)"
            (openScoreExplanation)="openScoreExplanation(card)"
          />

          @if (!isClosed(card)) {
            <hr class="my-3" />
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="text-uppercase text-muted font-11 fw-bold mb-0">
                {{ "kanban.bant.title" | translate }}
              </h6>
              <small class="text-muted"
                >{{ bantDone(card) }}/{{ bantFacts.length }}</small
              >
            </div>
            <div class="d-flex flex-wrap gap-1">
              @for (fact of bantFacts; track fact) {
                <button
                  type="button"
                  class="btn btn-sm rounded-pill"
                  [class.btn-soft-success]="card.bant?.[fact] === true"
                  [class.btn-outline-secondary]="card.bant?.[fact] !== true"
                  [disabled]="busy"
                  (click)="toggleBant(card, fact)"
                >
                  @if (card.bant?.[fact] === true) {
                    <i class="ri-check-line me-1"></i>
                  }
                  {{ BANT_FACT_LABEL[fact] | translate }}
                </button>
              }
            </div>

            <hr class="my-3" />
            <h6 class="text-uppercase text-muted font-11 fw-bold mb-2">
              {{ "kanban.cardDetails.isNextAction" | translate }}
            </h6>
            @if (card.nextAction?.dueAt) {
              <div
                class="alert mb-0"
                [ngClass]="'alert-' + urgencyContext(card)"
                role="alert"
              >
                @if (nextActionState(card); as state) {
                  <div class="d-flex align-items-center gap-1 fw-bold mb-1">
                    <i [class]="urgencyIcon(card)"></i>
                    {{ state.key | translate: state.params }}
                  </div>
                }
                <p class="text-dark mb-2">
                  {{ nextActionLabel(card) }} ·
                  <strong>{{
                    card.nextAction.dueAt | date: nextActionFormat(card)
                  }}</strong>
                  @if (card.nextAction.untimed) {
                    <small class="text-muted">· {{ "task.activity.untimed" | translate }}</small>
                  }
                </p>
                <!-- E4 RF-17, RF-24: the one conclusion dialog, and the postponement beside it. -->
                <div class="d-flex gap-2">
                  <button
                    type="button"
                    class="btn flex-grow-1"
                    [ngClass]="'btn-' + urgencyContext(card)"
                    [disabled]="busy"
                    (click)="concludeNextAction(card)"
                  >
                    <i class="ri-checkbox-circle-line me-1"></i
                    >{{ "task.activity.conclude" | translate }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    [disabled]="busy"
                    (click)="postponeNextAction(card)"
                    [attr.aria-label]="'kanban.radar.postponeNextAction' | translate"
                  >
                    <i class="ri-calendar-schedule-line me-1"></i
                    >{{ "task.activity.postpone" | translate }}
                  </button>
                </div>
              </div>
            } @else {
              <p class="text-muted font-13 mb-2">
                {{ "kanban.radar.noNextActionTooltip" | translate }}
              </p>
              <button
                type="button"
                class="btn btn-outline-primary w-100"
                (click)="openNextAction(card)"
              >
                <i class="ri-add-line me-1"></i
                >{{ "kanban.cardDetails.scheduleNextAction" | translate }}
              </button>
            }
          }

          <hr class="my-3" />
          <h6 class="text-uppercase text-muted font-11 fw-bold mb-2">
            {{ "kanban.cardDetails.tabActivities" | translate }}
          </h6>
          @if (!activitiesOf(card).length) {
            <p class="text-muted font-13 mb-0">
              {{ "kanban.cardDetails.activitiesEmpty" | translate }}
            </p>
          } @else {
            @for (activity of activitiesOf(card); track activity._id) {
              <div class="d-flex align-items-center gap-2 mb-2">
                <span
                  class="avatar-xs rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  [ngClass]="
                    'bg-soft-' +
                    activityContext(activity) +
                    ' text-' +
                    activityContext(activity)
                  "
                >
                  <i [class]="activityIcon(activity)"></i>
                </span>
                <div class="flex-grow-1 min-w-0">
                  <div class="font-13 fw-bold text-dark text-truncate">
                    {{ activityTypeLabel(activity) | translate }}
                    @if (activity.outcome) {
                      · {{ activityOutcomeLabel(activity) | translate }}
                    }
                  </div>
                  <small class="text-muted">
                    @if (activity.completedAt || activity.deadlineAt) {
                      {{
                        activity.completedAt ?? activity.deadlineAt
                          | date: activityFormat(activity)
                      }}
                    } @else {
                      <!-- No date: the same mark as the tasks area, since no day view lists it. -->
                      <span
                        class="badge campaign-badge is-neutral"
                        [matTooltip]="'task.activity.noDateHint' | translate"
                        [attr.aria-label]="'task.activity.noDateHint' | translate"
                      >
                        <i class="ri-calendar-close-line me-1"></i>{{ "task.activity.noDate" | translate }}
                      </span>
                    }
                    @if (activity.user?.name) {
                      · {{ activity.user.name }}
                    }
                  </small>
                </div>
              </div>
            }
          }
        </div>

        <div class="card-footer d-flex gap-2">
          @if (isClosed(card)) {
            <button
              type="button"
              class="btn btn-light flex-grow-1"
              (click)="convert()"
            >
              {{ "kanban.cardForm.title" | translate }}
            </button>
          }
          <button
            type="button"
            class="btn btn-outline-primary flex-grow-1"
            (click)="goToKanban(card)"
          >
            <i class="ri-arrow-right-line me-1"></i
            >{{ "attendance.panelInfo.goToOpportunity" | translate }}
          </button>
        </div>
      }
    </div>
  }
}
